import React, { useState, useEffect } from 'react'
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Spin,
  Row,
  Col,
  Card,
  message,
  Tag,
  Progress,
  Alert,
} from 'antd'
import {
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  TagOutlined,
  PercentageOutlined,
  BankOutlined, // 추가
} from '@ant-design/icons'
import backendApis from '../../utils/backendApis'
import commaNumber from 'comma-number'
import resize from '../../utils/resize'

const SummerBlackFridayBulkCancellationModal = ({
  showModal,
  setShowModal,
  dealType,
  promotionInfo,
  onSuccess,
}) => {
  const [selectedItems, setSelectedItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [totalItemsCount, setTotalItemsCount] = useState(0)
  const [displayItems, setDisplayItems] = useState([])
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [isAllSelected, setIsAllSelected] = useState(false)

  // 신청 완료된 상품 정보 가져오기
  const fetchAppliedItems = async () => {
    setIsLoading(true)
    try {
      // 신청 완료된 전체 상품 조건
      const itemCondition = {
        soldOut: false,
        outOfStock: { $ne: null },
        $or: [
          {
            itemFilters: {
              $elemMatch: {
                $and: [{ key: 'deal', value: dealType }],
              },
            },
          },
        ],
      }

      // 전체 상품 개수 가져오기
      const countResult = await backendApis.getSellerItemsCountByCondition({
        type: 'item',
        itemCondition,
      })

      let totalCount = 0
      if (countResult?.status === 200) {
        totalCount = countResult?.data || 0
        setTotalItemsCount(totalCount)
      } else {
        throw new Error('상품 개수를 가져오는데 실패했습니다')
      }

      // 상품이 없는 경우 처리
      if (totalCount === 0) {
        setDisplayItems([])
        setSelectedItems([])
        setSelectedItemIds([])
        setIsAllSelected(false)
        setIsLoading(false)
        return
      }

      // 모든 상품을 배치로 가져오기
      const batchSize = 500
      const totalPages = Math.ceil(totalCount / batchSize)
      let allItems = []

      for (let page = 1; page <= totalPages; page++) {
        const result =
          await backendApis.getSellerSegementedItemsInfoByCondition(
            {
              type: 'item',
              itemCondition,
            },
            page,
            batchSize,
          )

        if (result?.status === 2000) {
          const items = result?.data?.itemsInfo || []

          // 각 상품의 딜 정보도 함께 가져오기
          if (items.length > 0) {
            const itemIds = items.map((item) => item._id)
            const dealOptionsResult = await backendApis.getDealOptionsInfoArray(
              itemIds,
              dealType,
            )

            if (dealOptionsResult?.status === 200) {
              const dealOptionsData = dealOptionsResult?.data || []

              // 딜 정보 병합
              const itemsWithDealInfo = items.map((item) => {
                const dealInfo = dealOptionsData.find(
                  (deal) => deal.itemId?.toString() === item._id?.toString(),
                )
                return {
                  ...item,
                  dealTeamPurchasePrice: dealInfo?.dealTeamPurchasePrice,
                  dealDiscountRate:
                    dealInfo?.dealOptionsInfo?.discountRate ||
                    Math.round(
                      (1 -
                        dealInfo?.dealTeamPurchasePrice /
                          item.teamPurchasePrice) *
                        100,
                    ),
                }
              })

              allItems = [...allItems, ...itemsWithDealInfo]
            } else {
              allItems = [...allItems, ...items]
            }
          }
        }
      }

      setDisplayItems(allItems)
      setSelectedItems([])
      setSelectedItemIds([])
      setIsAllSelected(false)
    } catch (error) {
      console.error('Error fetching applied items:', error)
      message.error('상품 정보를 불러오는데 실패했습니다')
      setDisplayItems([])
      setSelectedItems([])
      setSelectedItemIds([])
      setIsAllSelected(false)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (showModal) {
      fetchAppliedItems()
    }
  }, [showModal])

  // 상품 선택 처리
  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      setSelectedItemIds([...selectedItemIds, itemId])
    } else {
      setSelectedItemIds(selectedItemIds.filter((id) => id !== itemId))
      setIsAllSelected(false)
    }
  }

  // 전체 선택/해제
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItemIds(displayItems.map((item) => item._id))
      setIsAllSelected(true)
    } else {
      setSelectedItemIds([])
      setIsAllSelected(false)
    }
  }

  // 취소 처리
  const handleSubmit = async () => {
    if (selectedItemIds.length === 0) {
      Modal.warning({
        title: '상품 선택 필요',
        content: '최소 1개 이상의 상품을 선택해주세요.',
      })
      return
    }

    Modal.confirm({
      title: '프로모션 신청 취소',
      icon: <WarningOutlined />,
      content: (
        <div>
          <p>
            선택한{' '}
            <span className='font-bold text-red-600'>
              {commaNumber(selectedItemIds.length)}개
            </span>{' '}
            상품의 프로모션 신청을 취소하시겠습니까?
          </p>
          <p className='text-sm text-gray-500 mt-2'>
            이 작업은 되돌릴 수 없습니다.
          </p>
        </div>
      ),
      okText: '취소하기',
      okType: 'danger',
      cancelText: '돌아가기',
      onOk: () => processCancel(),
    })
  }

  const processCancel = async () => {
    setIsSubmitting(true)

    try {
      const batchSize = 100
      let processedCount = 0
      let failedCount = 0

      // 진행률 표시 모달
      const progressModal = Modal.info({
        title: '프로모션 취소 중...',
        content: (
          <div>
            <Progress percent={0} />
            <p className='text-sm text-gray-600 mt-2'>
              {commaNumber(processedCount)} /{' '}
              {commaNumber(selectedItemIds.length)} 완료
            </p>
          </div>
        ),
        closable: false,
        maskClosable: false,
        okButtonProps: { style: { display: 'none' } },
      })

      // 선택된 상품들을 배치로 처리
      const selectedItems = displayItems.filter((item) =>
        selectedItemIds.includes(item._id),
      )

      for (let i = 0; i < selectedItems.length; i += batchSize) {
        const batch = selectedItems.slice(i, i + batchSize)

        // 각 배치의 상품들을 병렬로 취소
        const cancelPromises = batch.map((item) =>
          backendApis.deleteDealOptionsInfo(item._id, dealType),
        )

        const results = await Promise.allSettled(cancelPromises)

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value?.status === 200) {
            processedCount++
          } else {
            failedCount++
          }
        })

        // 진행률 업데이트
        const progress = Math.round(
          ((processedCount + failedCount) / selectedItemIds.length) * 100,
        )
        progressModal.update({
          content: (
            <div>
              <Progress
                percent={progress}
                status={failedCount > 0 ? 'exception' : 'active'}
              />
              <p className='text-sm text-gray-600 mt-2'>
                {commaNumber(processedCount)} /{' '}
                {commaNumber(selectedItemIds.length)} 완료
                {failedCount > 0 && (
                  <span className='text-red-500 ml-2'>
                    ({commaNumber(failedCount)} 실패)
                  </span>
                )}
              </p>
            </div>
          ),
        })
      }

      // 완료 처리
      progressModal.destroy()

      if (failedCount === 0) {
        message.success(
          `${commaNumber(
            processedCount,
          )}개 상품의 프로모션 신청이 취소되었습니다!`,
        )
        onSuccess && onSuccess()
        setShowModal(false)
      } else {
        Modal.warning({
          title: '부분 성공',
          content: `${commaNumber(
            processedCount,
          )}개 상품이 취소되었고, ${commaNumber(
            failedCount,
          )}개 상품이 실패했습니다.`,
        })
        onSuccess && onSuccess()
      }
    } catch (error) {
      Modal.error({
        title: '취소 실패',
        content: '프로모션 취소 중 오류가 발생했습니다. 다시 시도해주세요.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // 테이블 컬럼 정의
  const columns = [
    {
      title: (
        <Checkbox
          checked={isAllSelected && displayItems.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'select',
      key: 'select',
      width: 40,
      render: (_, record) => (
        <Checkbox
          checked={selectedItemIds.includes(record._id)}
          onChange={(e) => handleItemSelect(record._id, e.target.checked)}
        />
      ),
    },
    {
      title: '이미지',
      dataIndex: 'mainImageUris',
      key: 'image',
      width: 60,
      render: (mainImageUris) => (
        <img
          src={resize(mainImageUris?.[0])}
          alt='상품 이미지'
          className='w-12 h-12 object-cover rounded'
        />
      ),
    },
    {
      title: '상품명',
      dataIndex: 'itemTitle',
      key: 'itemTitle',
      ellipsis: true,
      render: (text) => <span className='text-xs'>{text}</span>,
    },
    {
      title: '원가',
      dataIndex: 'teamPurchasePrice',
      key: 'teamPurchasePrice',
      width: 80,
      render: (price) => (
        <span className='text-xs text-gray-500 line-through'>
          {commaNumber(price)}원
        </span>
      ),
    },
    {
      title: '프로모션가',
      dataIndex: 'dealTeamPurchasePrice',
      key: 'dealTeamPurchasePrice',
      width: 90,
      render: (price) => (
        <span className='text-xs font-semibold text-red-500'>
          {commaNumber(price)}원
        </span>
      ),
    },
    {
      title: '할인율',
      dataIndex: 'dealDiscountRate',
      key: 'dealDiscountRate',
      width: 100, // 70에서 90으로 증가
      render: (rate) => (
        <Tag color='red' className='text-xs'>
          {rate}% OFF
        </Tag>
      ),
    },
  ]

  return (
    <Modal
      visible={showModal}
      onCancel={() => {
        if (!isSubmitting) {
          setShowModal(false)
        }
      }}
      width={1200}
      style={{ top: 20 }}
      footer={null}
      closable={!isSubmitting}
      title={
        <div className='flex items-center'>
          <DeleteOutlined className='mr-2 text-xl text-gray-600' />
          <span className='text-lg font-bold'>
            올웨이즈 X 빗썸 여름 블랙프라이데이 일괄 취소
          </span>
        </div>
      }
    >
      {isLoading ? (
        <div className='flex justify-center items-center h-64'>
          <Spin size='large' tip='상품 정보를 불러오는 중...' />
        </div>
      ) : (
        <div className='space-y-4'>
          {/* 상품 선택 정보 */}
          <div className='bg-gray-50 rounded-lg px-4 py-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <CheckCircleOutlined className='text-blue-600' />
                <span className='font-medium'>취소 대상 상품</span>
                <Tag color='blue'>
                  전체 {commaNumber(totalItemsCount)}개 중{' '}
                  <span className='font-bold'>
                    {commaNumber(selectedItemIds.length)}
                  </span>
                  개 선택
                </Tag>
              </div>
              {selectedItemIds.length === totalItemsCount &&
                totalItemsCount > 0 && <Tag color='red'>전체 선택됨</Tag>}
            </div>
          </div>

          {/* 상품 목록 테이블 */}
          <div className='border border-gray-200 rounded-lg overflow-hidden'>
            <Table
              columns={columns}
              dataSource={displayItems}
              rowKey='_id'
              pagination={false}
              scroll={{ y: 400 }}
              size='small'
              loading={isLoading}
              locale={{
                emptyText: '신청된 상품이 없습니다',
              }}
            />
          </div>

          {/* 액션 버튼 */}
          <div className='flex justify-end gap-3 pt-2'>
            <Button
              size='middle'
              onClick={() => setShowModal(false)}
              disabled={isSubmitting}
            >
              닫기
            </Button>
            <Button
              type='primary'
              danger
              size='middle'
              onClick={handleSubmit}
              disabled={isSubmitting || selectedItemIds.length === 0}
              loading={isSubmitting}
              icon={<DeleteOutlined />}
            >
              선택한 {selectedItemIds.length}개 상품 취소
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default SummerBlackFridayBulkCancellationModal
